import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Tabs } from '@zillow/constellation';
import { Heading, Modal, Paragraph } from '@zillow/constellation-10';

import PostingActions from '../../../actions/PostingActions';

import ErrorMessageConstants from '../../../constants/ErrorMessageConstants';
import Constants from '../../../constants';

import ViewTourModal from '../ViewTourModal';
import SpinnerContainer from '../../SpinnerContainer';
import ZillowTours from './components/ZillowTours';
import ExternalTour from './components/ExternalTour';
import ModalFooter from '../ModalFooter';

import { useGetExternalTourProviders } from '../../../hooks/queries/useGetExternalTourProviders';

import { StyledTabContainer, StyledTabsList } from './StyledComponents';

const propTypes = {
  defaultVrModels: PropTypes.arrayOf(
    PropTypes.shape({
      vrModelId: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      createDateTimeUtc: PropTypes.string.isRequired,
      creatorName: PropTypes.string.isRequired,
      posterUrl: PropTypes.string,
      tourUrl: PropTypes.string,
      status: PropTypes.oneOf(['published', 'unpublished']),
    }),
  ),
  shouldLoadVrModels: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  tourType: PropTypes.oneOf(['floorPlan', 'unit', 'amenity', 'singleFamily']),
  initialSelectedVrModelId: PropTypes.string,
  initialExternalTour: PropTypes.shape({
    label: PropTypes.string,
    tourUrl: PropTypes.string,
  }),
  initialTab: PropTypes.oneOf(['zillow-tour-tab', 'external-tour-tab']),
  zillowDisplayStatus: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

const defaultProps = {
  zillowDisplayStatus: '',
  defaultVrModels: [],
  shouldLoadVrModels: true,
  tourType: 'floorPlan',
  initialSelectedVrModelId: undefined,
  initialExternalTour: {
    label: '',
    tourUrl: '',
  },
  initialTab: 'zillow-tour-tab',
};

const SF_NOT_PUBLISHED_TEXT =
  'We won’t able to add this 3d tour to your listing until the listing is live on our ' +
  'sites. Once this listing is live on our sites, please try this operation again.';

const SelectTourModal = ({
  filterOutVrModelIds,
  shouldLoadVrModels,
  onSubmit,
  onCancel,
  tourType,
  defaultVrModels,
  initialSelectedVrModelId,
  initialExternalTour,
  initialTab,
  zillowDisplayStatus,
  onClose,
}) => {
  const dispatch = useDispatch();

  const externalTourInputRef = useRef(null);

  const listingAlias = useSelector((state) => state.properties.currentProperty.listingAlias);

  const [isLoading, setIsLoading] = useState(shouldLoadVrModels);
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [selectedTabToConfirm, setSelectedTabToConfirm] = useState(initialTab);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [tourUrlErrorMessage, setTourUrlErrorMessage] = useState('');
  const [vrModels, setVrModels] = useState(defaultVrModels);
  const [selectedVrModelId, setSelectedVrModelId] = useState(initialSelectedVrModelId);
  const [externalTour, setExternalTour] = useState(initialExternalTour);
  const [showViewTourModal, setShowViewTourModal] = useState(false);
  const [vrModel, setVrModel] = useState();

  const isListingPublished = Constants.LISTING_STATUS.POSTED.toLowerCase() === zillowDisplayStatus;

  /* Prefetching data to avoid a loading spinner on the external tours tab */
  useGetExternalTourProviders();

  useEffect(() => {
    if (shouldLoadVrModels) {
      dispatch(PostingActions.property.vrModel.getAll(listingAlias))
        .then((loadedVrModels) => {
          const filteredVrModels =
            tourType === 'amenity'
              ? loadedVrModels.filter(
                  (vrModel) =>
                    vrModel.vrModelId === initialSelectedVrModelId ||
                    filterOutVrModelIds.indexOf(vrModel.vrModelId) === -1,
                )
              : loadedVrModels;
          const sortedVrModels = filteredVrModels.sort((a, b) => (a.createDateTimeUtc < b.createDateTimeUtc ? 1 : -1));
          setVrModels(sortedVrModels);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
          setErrorMessage('Cannot retrieve 3d tours, please try again later.');
        });
    }
  }, [tourType, shouldLoadVrModels]);

  const handleSubmit = () => {
    if (selectedTab === 'zillow-tour-tab') {
      if (tourType === 'singleFamily' && !isListingPublished) {
        setErrorMessage(SF_NOT_PUBLISHED_TEXT);
        return;
      }
    }
    setIsSubmitting(true);
    return onSubmit(selectedTab === 'zillow-tour-tab', selectedVrModelId, externalTour)
      .then(() => {
        onClose();
      })
      .catch((errMessage) => {
        setIsSubmitting(false);
        if (
          errMessage === ErrorMessageConstants.EXTERNAL_TOUR_INVALID_URL ||
          errMessage === ErrorMessageConstants.EXTERNAL_TOUR_ALREADY_EXIST ||
          errMessage === ErrorMessageConstants.EXTERNAL_TOUR_NOT_SUPPORTED_PROVIDER
        ) {
          setTourUrlErrorMessage(errMessage);
          // ZRM-10774: for error accessibility on screen readers, set focus to the tour url input
          externalTourInputRef.current && externalTourInputRef.current.focus();
        } else {
          setErrorMessage(errMessage);
        }
      });
  };

  const handleSelectTour = (id) => {
    setErrorMessage(null);
    const isUnselecting = selectedVrModelId === id;
    setSelectedVrModelId(isUnselecting ? null : id);
  };

  const handleViewTour = (vrModel) => {
    setVrModel(vrModel);
    setShowViewTourModal(true);
  };

  const handleUpdateExternalTour = (key, value) => {
    if (key === 'tourUrl' && tourUrlErrorMessage) {
      setTourUrlErrorMessage('');
    }
    if (errorMessage) {
      setErrorMessage('');
    }
    setExternalTour({
      ...externalTour,
      [key]: value,
    });
  };

  const handleConfirmTabChange = () => {
    setSelectedTab(selectedTabToConfirm);
    setSelectedVrModelId(initialSelectedVrModelId);
    setExternalTour(initialExternalTour);
    setTourUrlErrorMessage('');
    setErrorMessage('');
    setShowConfirmationModal(false);
  };

  const handleSelectTab = (selected) => {
    const hasPendingChanges =
      selectedVrModelId !== initialSelectedVrModelId ||
      externalTour.tourUrl !== initialExternalTour.tourUrl ||
      externalTour.label !== initialExternalTour.label;
    if (hasPendingChanges) {
      setShowConfirmationModal(true);
      setSelectedTabToConfirm(selected);
    } else {
      setSelectedTab(selected);
    }
  };

  const vrModelChange = initialSelectedVrModelId !== selectedVrModelId;
  const externalTourChange =
    initialExternalTour.tourUrl !== externalTour.tourUrl || initialExternalTour.label !== externalTour.label;
  const isSubmitDisabled =
    !!errorMessage ||
    !!tourUrlErrorMessage ||
    (!vrModelChange && !externalTourChange) ||
    (selectedTab === 'external-tour-tab' && (!externalTour.tourUrl || !externalTour.label));

  return (
    <Modal
      header={<Heading level={2}>3D tour</Heading>}
      body={
        isLoading ? (
          <SpinnerContainer />
        ) : (
          <StyledTabContainer>
            <Tabs onSelected={handleSelectTab} selected={selectedTab} fluid={true}>
              <StyledTabsList aria-label="tour tabs">
                <Tabs.Tab id="zillow-tour-tab">From Zillow 3D Home app</Tabs.Tab>
                <Tabs.Tab id="external-tour-tab">With a link</Tabs.Tab>
              </StyledTabsList>
              <Tabs.Panels>
                <Tabs.Panel aria-labelledby="zillow-tour-tab">
                  <ZillowTours
                    onSelectTour={handleSelectTour}
                    onViewTour={handleViewTour}
                    vrModels={vrModels}
                    selectedVrModelId={selectedVrModelId}
                    tourType={tourType}
                  />
                </Tabs.Panel>
                <Tabs.Panel aria-labelledby="external-tour-tab">
                  <ExternalTour
                    externalTour={externalTour}
                    onUpdateExternalTour={handleUpdateExternalTour}
                    tourUrlErrorMessage={tourUrlErrorMessage}
                    externalTourInputRef={externalTourInputRef}
                  />
                </Tabs.Panel>
              </Tabs.Panels>
            </Tabs>
            <Modal
              open={showConfirmationModal}
              body={<Paragraph>If you change tabs now, all changes will be lost. Is this what you want?</Paragraph>}
              footer={
                <ModalFooter
                  onCancel={() => setShowConfirmationModal(false)}
                  onSubmit={handleConfirmTabChange}
                  submitText="Yes, discard changes"
                />
              }
            />
            {showViewTourModal && <ViewTourModal vrModel={vrModel} onClose={() => setShowViewTourModal(false)} />}
          </StyledTabContainer>
        )
      }
      footer={
        <ModalFooter
          onCancel={onCancel}
          onSubmit={handleSubmit}
          submitText="Save"
          isSubmitting={isSubmitting}
          errorMessage={errorMessage}
          hasError={!!errorMessage}
          isSubmitDisabled={isSubmitDisabled}
        />
      }
      onOpenChange={(open) => !open && onClose()}
      open={true}
      size={{ base: 'fullScreen', lg: 'md' }}
    />
  );
};

SelectTourModal.propTypes = propTypes;
SelectTourModal.defaultProps = defaultProps;

export default SelectTourModal;
